// Constellations: captured moments of the song, stored in D1 (notes, players)
// and R2 (a short audio recording), titled by Workers AI.

import { BAR_MS, LANES, NOTE_NAMES, STEPS, type ModeName } from "../shared/music";
import type { Cell, PlayerInfo, ServerHome } from "../shared/protocol";
import type { AudioEngine } from "./audio/engine";
import type { Hud } from "./hud";
import { escapeHtml } from "./labels";

export interface ConstellationSummary {
  id: string;
  room: string;
  title: string;
  titleBy: string;
  createdAt: number;
  bar: number;
  root: number;
  mode: ModeName;
  hasRecording: boolean;
  plays: number;
  home: ServerHome;
  humans: number;
  places: string[];
  noteCount: number;
}

export interface Constellation extends ConstellationSummary {
  cells: Cell[];
  players: PlayerInfo[];
}

const RECORD_BARS = 4;

function pickMime(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

function timeAgo(ms: number): string {
  const s = Math.max(1, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

/** Records the next few bars of the live mix. Resolves with the audio blob. */
async function recordBars(engine: AudioEngine, bars: number): Promise<Blob | null> {
  const mime = pickMime();
  if (!mime || !engine.started) return null;
  const stream = engine.createRecordingStream();
  const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128_000 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const done = new Promise<void>((resolve) => (rec.onstop = () => resolve()));
  rec.start(500);
  await new Promise((r) => window.setTimeout(r, bars * BAR_MS + 1200));
  rec.stop();
  await done;
  return new Blob(chunks, { type: mime.split(";")[0] });
}

export async function captureMoment(roomName: string, engine: AudioEngine, hud: Hud, button: HTMLElement): Promise<void> {
  if (button.classList.contains("recording")) return;
  button.classList.add("recording");
  try {
    const res = await fetch(`/api/room/${encodeURIComponent(roomName)}/capture`, { method: "POST" });
    const data = (await res.json().catch(() => ({}))) as { id?: string; title?: string; uploadToken?: string; error?: string };
    if (!res.ok || !data.id) {
      hud.toast(escapeHtml(data.error ?? "The sky is closed right now (capturing needs the live server)."));
      return;
    }
    const link = `/c/${data.id}`;
    hud.toast(`✦ <b>“${escapeHtml(data.title ?? "Untitled")}”</b> — recording ${RECORD_BARS} bars…`, RECORD_BARS * BAR_MS + 2500);
    const blob = engine.started ? await recordBars(engine, RECORD_BARS) : null;
    if (blob && blob.size > 0 && data.uploadToken) {
      await fetch(`/api/constellations/${data.id}/recording?token=${data.uploadToken}`, {
        method: "PUT",
        headers: { "content-type": blob.type || "audio/webm" },
        body: blob,
      }).catch(() => null);
    }
    const url = new URL(link, location.href).toString();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard may be unavailable; the link is in the toast anyway.
    }
    hud.toast(`✦ Saved to the sky: <a href="${link}">“${escapeHtml(data.title ?? "Untitled")}”</a> <span class="muted">(link copied)</span>`, 9000);
  } catch {
    hud.toast("Couldn't reach the sky. Try again in a moment.");
  } finally {
    button.classList.remove("recording");
  }
}

/** A tiny picture of a constellation's notes, like a star chart. */
export function drawThumb(canvas: HTMLCanvasElement, c: { cells: Pick<Cell, "step" | "lane" | "hue">[] }): void {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const size = 54;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  const g = canvas.getContext("2d");
  if (!g) return;
  g.scale(dpr, dpr);
  const cx = size / 2;
  const cy = size / 2;
  g.fillStyle = "rgba(20, 30, 60, 0.6)";
  g.beginPath();
  g.arc(cx, cy, size / 2, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = "rgba(160,200,255,0.12)";
  g.beginPath();
  g.arc(cx, cy, 9, 0, Math.PI * 2);
  g.stroke();
  const pts: { x: number; y: number; hue: number }[] = [];
  for (const cell of c.cells) {
    const a = -Math.PI / 2 + ((cell.step + 0.5) / STEPS) * Math.PI * 2;
    const r = 11 + ((cell.lane + 0.5) / LANES) * 14;
    pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, hue: cell.hue });
  }
  pts.sort((p, q) => Math.atan2(p.y - cy, p.x - cx) - Math.atan2(q.y - cy, q.x - cx));
  g.strokeStyle = "rgba(180, 210, 255, 0.25)";
  g.lineWidth = 0.7;
  g.beginPath();
  pts.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
  g.stroke();
  for (const p of pts) {
    g.fillStyle = `hsl(${p.hue} 90% 70%)`;
    g.shadowColor = `hsl(${p.hue} 90% 60%)`;
    g.shadowBlur = 4;
    g.beginPath();
    g.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
    g.fill();
  }
}

export async function loadSky(list: HTMLElement): Promise<void> {
  list.innerHTML = `<div class="sky-empty">Reading the sky…</div>`;
  try {
    const res = await fetch("/api/constellations?limit=40");
    if (!res.ok) throw new Error(String(res.status));
    const data = (await res.json()) as { constellations: ConstellationSummary[] };
    if (!data.constellations.length) {
      list.innerHTML = `<div class="sky-empty">No constellations yet. Press <b>Capture</b> to save the first one.</div>`;
      return;
    }
    list.innerHTML = "";
    // Thumbnails need the notes; fetch each full record lazily.
    for (const c of data.constellations) {
      const a = document.createElement("a");
      a.className = "sky-item";
      a.href = `/c/${c.id}`;
      const canvas = document.createElement("canvas");
      const places = c.places.slice(0, 4).join(" · ");
      a.innerHTML = `<div></div><div><h3>${escapeHtml(c.title)}</h3><p>${NOTE_NAMES[c.root] ?? ""} ${escapeHtml(c.mode)} · ${c.noteCount} notes · ${c.humans} ${c.humans === 1 ? "human" : "humans"}${c.hasRecording ? " · ♫" : ""}<br>${escapeHtml(places)}<br>${timeAgo(c.createdAt)}</p></div>`;
      a.firstElementChild!.replaceWith(canvas);
      list.appendChild(a);
      drawThumb(canvas, { cells: [] });
      void fetch(`/api/constellations/${c.id}`)
        .then((r) => (r.ok ? (r.json() as Promise<Constellation>) : null))
        .then((full) => full && drawThumb(canvas, full))
        .catch(() => undefined);
    }
  } catch {
    list.innerHTML = `<div class="sky-empty">The sky is out of reach from here (it needs the live server).</div>`;
  }
}

export async function fetchConstellation(id: string): Promise<Constellation | null> {
  try {
    const res = await fetch(`/api/constellations/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    return (await res.json()) as Constellation;
  } catch {
    return null;
  }
}

/**
 * Draws recently captured constellations faintly into the night sky behind the
 * planet, like real constellations, each with its name. They avoid the middle
 * of the screen where the globe and ring live.
 */
export async function paintSkyConstellations(canvas: HTMLCanvasElement): Promise<void> {
  let items: Constellation[] = [];
  try {
    const res = await fetch("/api/constellations?limit=10");
    if (!res.ok) return;
    const list = ((await res.json()) as { constellations: ConstellationSummary[] }).constellations;
    const full = await Promise.all(list.slice(0, 10).map((c) => fetchConstellation(c.id)));
    items = full.filter((c): c is Constellation => !!c && c.cells.length >= 3);
  } catch {
    return;
  }
  if (!items.length) return;

  const draw = () => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const g = canvas.getContext("2d");
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;
    const keepOut = Math.min(w, h) * 0.48;
    const placed: { x: number; y: number }[] = [];
    let seed = 7;
    const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    for (const c of items) {
      // Find a free spot away from the planet and other constellations.
      let x = 0;
      let y = 0;
      let ok = false;
      for (let tries = 0; tries < 40 && !ok; tries++) {
        x = 60 + rand() * (w - 120);
        y = 110 + rand() * (h - 260);
        ok = Math.hypot(x - cx, y - cy) > keepOut && placed.every((p) => Math.hypot(p.x - x, p.y - y) > 150);
      }
      if (!ok) continue;
      placed.push({ x, y });
      const scale = 2.3;
      const pts = c.cells
        .map((cell) => {
          const a = -Math.PI / 2 + ((cell.step + 0.5) / STEPS) * Math.PI * 2;
          const r = (10 + ((cell.lane + 0.5) / LANES) * 14) * scale;
          return { x: x + Math.cos(a) * r, y: y + Math.sin(a) * r, hue: cell.hue, a };
        })
        .sort((p, q) => p.a - q.a);
      g.strokeStyle = "rgba(170, 200, 255, 0.10)";
      g.lineWidth = 0.8;
      g.beginPath();
      pts.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
      g.closePath();
      g.stroke();
      for (const p of pts) {
        const grad = g.createRadialGradient(p.x, p.y, 0, p.x, p.y, 5);
        grad.addColorStop(0, `hsla(${p.hue}, 80%, 85%, 0.55)`);
        grad.addColorStop(1, `hsla(${p.hue}, 80%, 70%, 0)`);
        g.fillStyle = grad;
        g.beginPath();
        g.arc(p.x, p.y, 5, 0, Math.PI * 2);
        g.fill();
      }
      g.font = "italic 10.5px ui-sans-serif, system-ui, sans-serif";
      g.textAlign = "center";
      g.fillStyle = "rgba(170, 190, 230, 0.32)";
      g.fillText(c.title.length > 34 ? `${c.title.slice(0, 33)}…` : c.title, x, y + 34 * scale);
    }
  };
  draw();
  let t: number | undefined;
  window.addEventListener("resize", () => {
    window.clearTimeout(t);
    t = window.setTimeout(draw, 200);
  });
}
